import { useCallback, useEffect, useState } from "react";

import { DialogContent, TextField } from "@mui/material";
import capitalize from "lodash/capitalize";

import { globalText } from "../../assets/texts";
import SimpleDialog from "../SimpleDialog";
import { defaultDescriptionLength, defaultNameLength } from "./EditableResourceHeader";

type ResourceEditDialogProps = {
  open: boolean;
  onClose: () => void;
  onSave: (values: { name: string; description?: string }) => void;
  initialValues: {
    name: string;
    description?: string;
  };
  maxNameLength?: number;
  maxDescriptionLength?: number;
  resourceName: string;
  hideDescription?: boolean;
  customActionLabel?: string;
};

const ResourceEditDialog = ({
  open,
  onClose,
  onSave,
  initialValues,
  maxNameLength = defaultNameLength,
  maxDescriptionLength = defaultDescriptionLength,
  resourceName = "resource",
  hideDescription = false,
  customActionLabel,
}: ResourceEditDialogProps) => {
  const [name, setName] = useState(initialValues.name);
  const [description, setDescription] = useState(initialValues.description || "");
  const [loading, setLoading] = useState(false);
  const dialogHeader = `Edit ${resourceName} ${hideDescription ? "name" : "details"}`;

  useEffect(() => {
    if (open) {
      setName(initialValues.name);
      setDescription(initialValues.description || "");
    }
  }, [open, initialValues]);

  const hasChanges =
    name.trim() !== initialValues.name.trim() ||
    (!hideDescription && description.trim() !== (initialValues.description || "").trim());

  const handleSave = useCallback(() => {
    setLoading(true);
    onSave({ name, description: hideDescription ? undefined : description });
    setLoading(false);
  }, [name, description, onSave, hideDescription]);

  const handleChangeName = useCallback((event) => {
    setName(event.target.value);
  }, []);

  const handleChangeDescription = useCallback((event) => {
    setDescription(event.target.value);
  }, []);

  const disableConfirm = !name.trim() || loading || !hasChanges;

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === "Enter" && !disableConfirm) {
        event.preventDefault();
        handleSave();
      }
    },
    [handleSave, disableConfirm]
  );

  return (
    <SimpleDialog
      open={open}
      title={dialogHeader}
      onConfirm={handleSave}
      onCancel={onClose}
      confirmButtonText={customActionLabel || globalText.SAVE}
      disableConfirm={disableConfirm}
      dialogProps={{ fullWidth: true }}
      titleCy="resource-edit-dialog-title"
      confirmButtonCy="resource-edit-dialog-save"
    >
      <DialogContent sx={{ mt: 2 }} data-cy="resource-edit-dialog-content">
        <TextField
          autoFocus
          variant="outlined"
          fullWidth
          value={name}
          onChange={handleChangeName}
          onKeyDown={handleKeyDown}
          label={globalText.NAME}
          placeholder={`${capitalize(resourceName)} name`}
          required
          slotProps={{
            input: {
              maxLength: maxNameLength,
              "data-cy": "resource-name-input",
            },
          }}
          sx={{ mb: hideDescription ? 0 : 2, mt: 1 }}
        />
        {!hideDescription && (
          <TextField
            variant="outlined"
            fullWidth
            value={description}
            onChange={handleChangeDescription}
            onKeyDown={handleKeyDown}
            label={globalText.DESCRIPTION}
            placeholder={globalText.ADD_DESCRIPTION}
            slotProps={{
              input: {
                maxLength: maxDescriptionLength,
                "data-cy": "resource-description-input",
              },
            }}
          />
        )}
      </DialogContent>
    </SimpleDialog>
  );
};

export default ResourceEditDialog;
